/**
 * @file 用户信息 store
 * @author
 */

import {makeObservable, observable, flow, action, computed} from 'mobx';
import Cookies from 'js-cookie';
import {isNil} from 'lodash';
import {
    getUserInfo,
    logout,
    getLoginUrl,
    getIsInitial,
    finishStep,
    getApiKeyInfo,
    putApiKeyInfo,
    checkIsLogin,
    getSsoTicket,
    ssoSignByTicket,
    type WaUserInfo
} from '@/api/login';
import {fetchChargeAccountInfo} from '@/api/billing';
import {checkStatusJob} from '@/api/console';
import {handleRequestError} from '@/utils/errorHandler';
import {jobIdSyncService} from '@/utils/JobIdSyncService';
import {getSubscriptionPlanText} from '@/utils';
import {MigrationPhase, getMigrationPhase} from '@/utils/migration';

class UserStore {
    /** 是否登陆 */
    hasLogin = false;
    apiKey: string = '';
    /** 用户id */
    userId: string = '';
    /** 用户名称 */
    userName: string = '';
    /** 用户头像 */
    avatar: string = '';
    /** 用户角色 */
    role: string = '';
    logoPath: string = '';
    /** 用户权限 */
    permissions: string[] = [];
    /** 用户是否是新用户 */
    new_user_flag: boolean = false;
    in_white_list: number | null = null;
    /** 订阅套餐名称 */
    subscriptionPlan: string = '';
    subscriptionPlanText: string = '';
    /** 是否是企业版本 */
    isEnterprise: boolean = false;
    /** 账号类型：1=主账号 / 2=子账号 */
    accountType: number | null = null;

    /** 管理平台下线倒计时（剩余天数，总周期 30 天），null 表示后端未下发 */
    workspaceOfflineDays: number | null = null;

    /** 每日可用资源点 */
    dailyCredit: number | null = null;
    /** 充值地址 */
    rechargeUrl: {ws: string, wa: string} = {ws: '', wa: ''};

    /** 子用户：是否是子用户 */
    iSubUser: boolean = false;

    /** 子用户：剩余可用资源点 */
    subUserCreditRemaining: number | undefined = undefined;
    /** 子用户：分配额度 */
    subUserCreditQuota: number | undefined = undefined;

    /** 样板空间 */
    template_id: string | null = null;

    /** 是否是首次登陆 */
    is_first_login: boolean = false;

    /** 样板作业ID */
    templateJobId: string | null = null;

    /** 是否第一次进入步骤一（原始算法） */
    isFirstTimeStepOne: boolean = false;

    /** 是否第一次进入步骤二（评估资源） */
    isFirstTimeStepTwo: boolean = false;

    /** 是否第一次进入任务运行页（冷启动/迭代演化） */
    isFirstTimeTaskRunning: boolean = false;

    /** 标记全局新手引导弹框 */
    isShowGuideModal = false;

    /** 是否第一次展示新手引导弹框 */
    isFirstShowGuideModal = true;

    /** 复制任务时，是否跳过弹窗直接复制 */
    jobCopyNoPrompt: boolean = false;

    /** 是否开启文件打开提示 */
    fileOpenNoPrompt: boolean = false;

    /**
     * 冷启动功能移除提示，记录每个任务的提示状态，key=jobId，value=true表示已提示过
     * 种植弹窗关闭提示，记录每个任务的提示状态，key=task_terminated_jobID value=true表示已提示过
     * */
    coldStartCheckStates: Record<string, boolean> = {};

    /** 最大任务数量限制 */
    maxJobCount: number = 3;

    /** 数据集上传配置 */
    datasetUploadConfig: {maxFileCount: number, maxFileSizeKB: number, maxAllfilesSize: number} = {
        maxFileCount: 100,
        maxFileSizeKB: 512000,
        maxAllfilesSize: 20971520
    };
    /** 续跑演化 优质解提示 */
    isFirstShowTopSolutionTip: boolean = true;
    subUserCreditExpireAt: string | undefined;

    /** userinfo 下发的 wa 用户资料，V2.5 升级弹窗状态（v25_target / v25_agreed）也在这里 */
    waUserInfo: WaUserInfo = {};

    /** 账号计费信息是否已拉取过（无论成功失败）：套餐未就绪时不能按套餐区分弹窗形态 */
    accountInfoLoaded: boolean = false;

    constructor() {
        makeObservable(this, {
            hasLogin: observable,
            userId: observable,
            logoPath: observable,
            userName: observable,
            avatar: observable,
            role: observable,
            permissions: observable,
            new_user_flag: observable,
            is_first_login: observable,
            template_id: observable,
            templateJobId: observable,
            isFirstTimeStepOne: observable,
            isFirstTimeStepTwo: observable,
            isFirstTimeTaskRunning: observable,
            isShowGuideModal: observable,
            isFirstShowGuideModal: observable,
            jobCopyNoPrompt: observable,
            fileOpenNoPrompt: observable,
            coldStartCheckStates: observable,
            maxJobCount: observable,
            datasetUploadConfig: observable,
            apiKey: observable,
            in_white_list: observable,
            subscriptionPlan: observable,
            subscriptionPlanText: observable,
            dailyCredit: observable,
            rechargeUrl: observable,
            isEnterprise: observable,
            accountType: observable,
            workspaceOfflineDays: observable,
            subUserCreditExpireAt: observable,
            subUserCreditQuota: observable,
            subUserCreditRemaining: observable,
            iSubUser: observable,
            waUserInfo: observable,
            accountInfoLoaded: observable,
            setUserInfo: action.bound,
            setWaUserInfo: action.bound,
            markCheckState: flow.bound,
            setTemplateId: action.bound,
            fetchUserInfo: flow.bound,
            fetchAccountInfo: flow.bound,
            checkLoginStatus: flow.bound,
            getSsoTicketInfo: flow.bound,
            ssoSignWithTicket: flow.bound,
            trySsoLogin: flow.bound,
            logout: flow.bound,
            fetchTemplateInfo: flow.bound,
            setStepVisited: action.bound,
            setTemplateJobId: action.bound,
            markStepFinished: flow.bound,
            refreshApikeyInfo: flow.bound,
            fetchApikeyInfo: flow.bound,
            setShowGuideModal: action.bound,
            setIsFirstShowGuideModal: action.bound,
            setFirstShowTopSolutionTip: action.bound,
            isPro: computed,
            isFree: computed,
            needV25UpgradeConfirm: computed,
            isEnterpriseAdmin: computed,
            enterprisePlanText: computed,
            migrationPhase: computed,
            isMigrationRestricted: computed
        });
    }

    /** 判断是否为 Pro 用户 */
    get isPro(): boolean {
        return this.subscriptionPlan?.toLowerCase() === 'pro';
    }

    /** 判断是否为 Free 用户 */
    get isFree(): boolean {
        return this.subscriptionPlan?.toLowerCase() === 'free';
    }

    /**
     * Pro 老用户是否还需要确认 V2.5 升级
     * v25_agreed 为 false 表示只留了手机号，按 PRD 仍要继续弹；未命中名单的用户永远不弹
     */
    get needV25UpgradeConfirm(): boolean {
        return this.isPro && Boolean(this.waUserInfo.v25_target) && this.waUserInfo.v25_agreed !== true;
    }

    /** 判断是否显示企业管理平台入口：enterprise 主账号才显示 */
    get isEnterpriseAdmin(): boolean {
        return this.isEnterprise && this.accountType === 1;
    }

    /** 企业版显示文案：主用户/子用户区分；非企业版返回 subscriptionPlanText */
    get enterprisePlanText(): string {
        if (this.isEnterprise) {
            return this.iSubUser ? '企业版-子用户' : '企业版-主用户';
        }
        return this.subscriptionPlanText || '—';
    }

    /** 管理平台下线迁移所处阶段 */
    get migrationPhase(): MigrationPhase {
        return getMigrationPhase(this.workspaceOfflineDays);
    }

    /** 是否处于受限运行期（及之后）：禁止新建任务、复制任务、开启/续跑演化 */
    get isMigrationRestricted(): boolean {
        return this.migrationPhase === MigrationPhase.RESTRICTED
            || this.migrationPhase === MigrationPhase.SHUTDOWN;
    }

    /** 更新用户信息 */
    setUserInfo(data: Record<string, any>) {
        this.setBasicUserInfo(data);
        this.setUserFlags(data);
        this.setUserConfig(data.user_config || {});
    }

    /** 更新样板间ID */
    setTemplateId(template_id: string | null) {
        this.template_id = template_id;
    }

    /** 合并 users/info 回传的 wa 用户资料，保持 v25_agreed / phone 与后端一致 */
    setWaUserInfo(waUserInfo?: WaUserInfo) {
        this.waUserInfo = {...this.waUserInfo, ...(waUserInfo || {})};
    }

    setTemplateJobId(job_id: string | null) {
        this.templateJobId = job_id;
    }

    /** 设置优质解提示是否首次显示 */
    setFirstShowTopSolutionTip(value: boolean) {
        this.isFirstShowTopSolutionTip = value;
    }

    /** 判断指定任务的冷启动提示是否已显示过 */
    isColdStartPrompted(jobId: string): boolean {
        return this.coldStartCheckStates[jobId] === true;
    }

    /** 判断指定任务的任务终止提示是否已显示过 */
    isTaskTerminatedPrompted(jobId: string): boolean {
        return this.coldStartCheckStates[`task_terminated_${jobId}`] === true;
    }

    /** 标记步骤已访问 */
    setStepVisited(step: 'one' | 'two' | 'taskRunning') {
        if (step === 'one') {
            this.isFirstTimeStepOne = false;
        } else if (step === 'two') {
            this.isFirstTimeStepTwo = false;
        } else if (step === 'taskRunning') {
            this.isFirstTimeTaskRunning = false;
        }
    }

    setShowGuideModal(val: boolean) {
        this.isShowGuideModal = val;
    }

    setIsFirstShowGuideModal(val: boolean) {
        this.isFirstShowGuideModal = val;
    }

    * fetchTemplateInfo(template_id: string) {
        try {
            if (template_id) {
                this.template_id = template_id;
            }

            const res: ApiResponse = yield getIsInitial({template_id: this.template_id});

            if ((res.code === 0 || res.code === '0') && res.data && res.data.job_id) {
                // 写入地址栏 jobId（先清除旧的，再设置新的）
                jobIdSyncService.clearJobId();
                jobIdSyncService.setJobId(res.data.job_id);

                // 保存到 store
                this.templateJobId = res.data.job_id;

                return res;
            }

            throw new Error('获取样板间信息失败！');
        } catch (err) {
            throw err;
        }
    }

    /** 获取账号计费信息 */
    * fetchAccountInfo() {
        if (!this.userId) {
            this.resetAccountInfo();
            this.accountInfoLoaded = true;
            return null;
        }

        try {
            const accountRes: ApiResponse = yield fetchChargeAccountInfo({userId: this.userId});
            if (this.isSuccessResponse(accountRes) && accountRes.data) {
                this.setAccountInfo(accountRes.data);
                return accountRes.data;
            }
            this.resetAccountInfo();
            return null;
        } catch (error) {
            this.resetAccountInfo();
            return null;
        } finally {
            this.accountInfoLoaded = true;
        }
    }

    /**
     * 落库一次性提示状态（user_config.user_check_states.check_states）
     * 与冷启动/复制任务提示共用一份数据，成功后同步本地，失败静默
     */
    * markCheckState(checkKey: string) {
        try {
            const res: ApiResponse = yield checkStatusJob({check_key: checkKey, status: true});
            if (this.isSuccessResponse(res)) {
                this.coldStartCheckStates = {...this.coldStartCheckStates, [checkKey]: true};
            }
        } catch (err) {
            console.error('标记提示状态失败:', checkKey, err);
        }
    }

    /** 获取用户信息 */
    * fetchUserInfo(params?: {invitecode?: string}) {
        try {
            const res: ApiResponse = yield getUserInfo(params);
            if (!this.isSuccessResponse(res) || !res.data) {
                this.hasLogin = false;
                this.resetAccountInfo();
                return null;
            }

            this.setUserInfo(res.data);

            // 用户信息成功后拉取账号订阅信息，用于侧边栏版本展示。
            yield this.fetchAccountInfo();

            return res.data;
        } catch (err) {
            this.hasLogin = false;
            this.resetAccountInfo();
            throw err;
        }
    }

    /** 检查后端登录态 */
    * checkLoginStatus() {
        const res: ApiResponse = yield checkIsLogin();
        const isSuccess = res?.code === 0 || res?.code === '0';
        return Boolean(isSuccess && res?.data?.isLogin === true);
    }

    /** 获取 SSO ticket */
    * getSsoTicketInfo() {
        const res: ApiResponse = yield getSsoTicket({product: 'workspace'});
        const isSuccess = res?.code === 0 || res?.code === '0';
        if (!isSuccess || !res?.data?.ticket) {
            return null;
        }
        return res.data.ticket as string;
    }

    /** 使用 SSO ticket 换取登录态 */
    * ssoSignWithTicket(ticket: string) {
        if (!ticket) {
            return false;
        }
        const res: ApiResponse = yield ssoSignByTicket(ticket);
        return Boolean(res?.code === 0 || res?.code === '0');
    }

    /** SSO 登录兜底流程：拿 ticket -> sign */
    * trySsoLogin() {
        try {
            const ticket: string | null = yield this.getSsoTicketInfo();
            if (!ticket) {
                return false;
            }
            const signed: boolean = yield this.ssoSignWithTicket(ticket);
            return signed;
        } catch (err) {
            return false;
        }
    }

    /** 获取用户信息 */
    * fetchApikeyInfo() {
        try {
            const res: ApiResponse = yield getApiKeyInfo();
            if ((res.code === 0 || res.code === '0') && res.data) {
                this.apiKey = res.data.apiKey ?? '';
                return res.data;
            } else {
                this.hasLogin = false;
                return null;
            }
        } catch (err) {
            this.hasLogin = false;
            throw err;
        }
    }

    /** 获取用户信息 */
    * refreshApikeyInfo() {
        try {
            const res: ApiResponse = yield putApiKeyInfo();
            if ((res.code === 0 || res.code === '0') && res.data) {
                this.apiKey = res.data.apiKey ?? '';
                return res.data;
            } else {
                return null;
            }
        } catch (err) {
            throw err;
        }
    }

    /** 登出 */
    * logout() {
        try {
            const res: ApiResponse = yield logout();
            if (res.code === 0 || res.code === '0') {
                this.hasLogin = false;
                // 清除所有本地存储（包括 selectedJobId、单页面守卫相关的 key 等）
                localStorage.clear();
                // 同步清除 URL 上的 jobId 查询参数
                jobIdSyncService.clearJobId();
                Cookies.remove('fm_login_userid');
                Cookies.remove('fm_login_session');
                window.location.href = getLoginUrl();
            }
        } catch (err) {
            handleRequestError({error: err});
        }
    }

    /**
     * 标记引导步骤已完成
     * 仅当用户首次访问该步骤时才发送请求（后端字段 1=首次，0=已完成）
     */
    * markStepFinished(step: 'one' | 'two' | 'taskRunning') {
        try {
            const stepConfig = {
                one: {flag: this.isFirstTimeStepOne, number: 1 as const},
                two: {flag: this.isFirstTimeStepTwo, number: 2 as const},
                taskRunning: {flag: this.isFirstTimeTaskRunning, number: 3 as const} // TODO: 后端字段待定
            };

            const config = stepConfig[step];
            if (!config.flag) {
                return; // 已经完成过，不重复发送
            }

            const res: ApiResponse = yield finishStep(config.number);

            if (res.code === 0 || res.code === '0') {
                const stepStateMap = {
                    one: () => {this.isFirstTimeStepOne = false;},
                    two: () => {this.isFirstTimeStepTwo = false;},
                    taskRunning: () => {this.isFirstTimeTaskRunning = false;}
                };
                stepStateMap[step]();
            }
        } catch (err) {
            // 静默失败，不影响用户关闭引导页
            console.error('标记步骤完成失败:', err);
        }
    }

    /** 统一判断接口是否成功 */
    private isSuccessResponse(res?: ApiResponse) {
        return res?.code === 0 || res?.code === '0';
    }

    /** 重置账号订阅信息（空状态） */
    private resetAccountInfo() {
        this.subscriptionPlan = '';
        this.dailyCredit = null;
        this.rechargeUrl = {ws: '', wa: ''};
        this.subscriptionPlanText = '';
        this.isEnterprise = false;
    }

    /** 更新账号订阅信息 */
    private setAccountInfo(data?: Record<string, any>) {
        if (!data) {
            this.resetAccountInfo();
            return;
        }

        this.isEnterprise = data.subscription_plan === 'Enterprise';
        this.subscriptionPlan = data.subscription_plan || '';
        this.subscriptionPlanText = getSubscriptionPlanText(data.subscription_plan);
        this.dailyCredit = isNil(data.daily_credit) ? null : Number(data.daily_credit);
        this.rechargeUrl = {
            ws: data.recharge_url?.ws || '',
            wa: data.recharge_url?.wa || ''
        };

        this.iSubUser = data.is_sub_user ?? false;

        this.subUserCreditRemaining = isNil(data.sub_user_credit_remaining)
            ? 0
            : Number(data.sub_user_credit_remaining);
        this.subUserCreditQuota = isNil(data.sub_user_credit_quota)
            ? 0
            : Number(data.sub_user_credit_quota);
        this.subUserCreditExpireAt = data.sub_user_credit_expire_at || undefined;
    }

    /** 更新用户配额配置 */
    private updateUserQuota(quotaConfig: any) {
        if (!quotaConfig) {
            return;
        }

        this.maxJobCount = isNil(quotaConfig.max_job_count) ? 3 : quotaConfig.max_job_count;

        const uploadDataset = quotaConfig.file_upload?.upload_dataset;
        if (!uploadDataset) {
            return;
        }

        this.datasetUploadConfig = {
            maxFileCount: uploadDataset.file_max_count ?? 100,
            maxFileSizeKB: uploadDataset.file_single_max_size ?? 512000,
            maxAllfilesSize: uploadDataset.files_max_size ?? 20971520
        };
    }


    /** 设置基础用户信息 */
    private setBasicUserInfo(data: Record<string, any>) {
        this.userId = data.user_id ?? '';
        this.userName = data.user_name ?? '';
        this.avatar = data.avatar ?? '';
        this.role = data.role ?? '';
        this.permissions = data.permissions ?? [];
        this.new_user_flag = !!data.new_user_flag;
        this.hasLogin = true;
        this.in_white_list = data.in_white_list;
        this.accountType = isNil(data.account_type) ? null : Number(data.account_type);
        this.workspaceOfflineDays = isNil(data.workspace_offline_days)
            ? null
            : Number(data.workspace_offline_days);
        this.waUserInfo = data.waUserInfo ?? {};
    }

    /** 设置用户标志位 */
    private setUserFlags(data: Record<string, any>) {
        // 后端字段定义: 1=首次, 0=已完成
        this.is_first_login = data.is_first_login === 1;
        this.isFirstTimeStepOne = data.is_first_time_step_one === 1;
        this.isFirstTimeStepTwo = data.is_first_time_step_two === 1;
        this.isFirstTimeTaskRunning = data.is_first_time_task_running === 1;
    }

    /** 设置用户配置 */
    private setUserConfig(userConfig: Record<string, any>) {
        const {check_states} = userConfig.user_check_states || {};

        // 使用 lodash isNil 判断是否为 undefined 或 null
        if (!isNil(check_states?.job_copy_no_prompt)) {
            this.jobCopyNoPrompt = check_states.job_copy_no_prompt;
        }

        if (!isNil(check_states?.file_open_no_prompt)) {
            this.fileOpenNoPrompt = check_states.file_open_no_prompt;
        }

        if (!isNil(check_states)) {
            this.coldStartCheckStates = {...this.coldStartCheckStates, ...check_states};
        }

        if (!isNil(check_states?.isFirstShowTopSolutionTip)) {
            this.isFirstShowTopSolutionTip = check_states.isFirstShowTopSolutionTip;
        }


        this.updateUserQuota(userConfig.user_quota || {});
    }
}

export default UserStore;
